import type {
  Entry as UserEntry,
  GlucoseSubEntry,
  GlucoseEvent,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { ActivityType } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import type { ReactNode } from "react"
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import type { DeepReadonlyObject } from "rxdb"
import { useDebounceCallback } from "usehooks-ts"
import { RecentReadingsContext } from "./RecentReadingsContext"
import type { RecentReadingsContextValue } from "./RecentReadingsContext"
import { useThisMinute } from "src/hooks/time"
import { useFindUserEntries } from "src/hooks/userEntry"
import { useFindUserEvents } from "src/hooks/userEvent"
import {
  createEntryQueryObjectForType,
  createEventQueryObjectForType,
  glucoseEventToReading,
  glucoseSubEntryToReading,
} from "src/utils"

interface Props {
  children: ReactNode;
}

export function RecentReadingsContextProvider(props: Props) {
  const [
    recentReadings,
    setRecentReadings,
  ] = useState({} as RecentReadingsContextValue["readings"])

  const [
    recentReadingsInitialized,
    setRecentReadingsInitialized,
  ] = useState<boolean>(false)

  const thisMinute = useThisMinute()

  const afterTimeString = useMemo(
    () => dayjs(thisMinute)
      .subtract(
        15,
        "minutes",
      )
      .toISOString(),
    [thisMinute],
  )

  const beforeTimeString = useMemo(
    () => dayjs(thisMinute)
      .add(
        1,
        "minute",
      )
      .toISOString(),
    [thisMinute],
  )


  const nextGlucoseEventSelector = useMemo(
    () => createEventQueryObjectForType(
      undefined,
      ActivityType.Glucose,
      {
        selector: {
          started_at: {
            $gte: afterTimeString,
            $lte: beforeTimeString,
          },
        },
        sort: [{ started_at: "desc" as const }],
      },
    ),
    [
      afterTimeString,
      beforeTimeString,
    ],
  )

  const nextGlucoseEntrySelector = useMemo(
    () => createEntryQueryObjectForType(
      undefined,
      ActivityType.Glucose,
      {
        selector: {
          started_at: {
            $gte: afterTimeString,
            $lte: beforeTimeString,
          },
        },
        sort: [{ started_at: "desc" as const }],
      },
    ),
    [
      afterTimeString,
      beforeTimeString,
    ],
  )

  const {
    result: recentGlucoseEvents,
    isFetching: isFetchingEvents,
  } = useFindUserEvents(nextGlucoseEventSelector)

  const {
    result: recentEntries,
    isFetching: isFetchingEntries,
  } = useFindUserEntries(nextGlucoseEntrySelector)

  const updateRecentReadings = useCallback(
    (
      fetchingEntries: boolean,
      fetchingEvents: boolean,
      entries: DeepReadonlyObject<UserEntry>[],
      glucoseEvents: DeepReadonlyObject<GlucoseEvent>[],
    ) => {
      const readings: RecentReadingsContextValue["readings"] = {}

      glucoseEvents.forEach((glucoseEvent) => {
        if (readings[glucoseEvent.account_id]) {
          return
        }

        readings[glucoseEvent.account_id] = glucoseEventToReading(glucoseEvent)
      })

      entries.forEach((entry) => {
        // skip if this entry is not newer than the most recent reading
        if (
          readings[entry.account_id] &&
          dayjs(readings[entry.account_id].startedAt).isAfter(
            entry.started_at,
          )
        ) {
          return
        }

        const glucoseSubEntry = entry.subentries.find(
          (subentry) => subentry.subentry_type === ActivityType.Glucose,
        ) as GlucoseSubEntry | undefined

        // skip if this entry does not have any glucose sub entries
        if (!glucoseSubEntry) {
          return
        }

        readings[entry.account_id] = glucoseSubEntryToReading(
          entry.started_at,
          glucoseSubEntry,
        )
      })

      setRecentReadings(readings)
      if (Object.keys(readings).length || (!fetchingEntries && !fetchingEvents)) {
        setRecentReadingsInitialized(true)
      }
    },
    [
      setRecentReadings,
      setRecentReadingsInitialized,
    ],
  )

  const debouncedUpdateRecentReadings = useDebounceCallback(
    updateRecentReadings,
    500,
  )

  useEffect(
    () => {
      debouncedUpdateRecentReadings(
        isFetchingEntries,
        isFetchingEvents,
        recentEntries,
        recentGlucoseEvents as GlucoseEvent[],
      )
    },
    [
      debouncedUpdateRecentReadings,
      isFetchingEntries,
      isFetchingEvents,
      recentEntries,
      recentGlucoseEvents,
    ],
  )

  const value = useMemo(
    () => ({
      readings: recentReadings,
      initialized: recentReadingsInitialized,
    }),
    [
      recentReadings,
      recentReadingsInitialized,
    ],
  )

  return (
    <RecentReadingsContext.Provider value={value}>
      {props.children}
    </RecentReadingsContext.Provider>
  )
}
